import { useMemo } from 'react';
import PageHero from '../components/PageHero.jsx';
import InfoIcon from '../components/InfoIcon.jsx';
import Pill from '../components/Pill.jsx';
import { EVENTS } from '../data/events.js';
import './Events.css';

const CATEGORY_ORDER = [
  'tradition',
  'fundraiser',
  'dance',
  'pep-rally',
  'spirit-week',
  'ceremony',
  'curriculum',
];

const CATEGORY_LABELS = {
  tradition:     'Eagle Traditions',
  fundraiser:    'Fundraisers & Library Club',
  dance:         'Dances',
  'pep-rally':   'Pep Rallies',
  'spirit-week': 'Spirit Weeks',
  ceremony:      'Ceremonies',
  curriculum:    'Curriculum Events',
};

const AUDIENCE_LABELS = {
  'all-students': 'All students',
  '6th-only':     '6th grade',
  '7th-8th-only': '7th & 8th grade',
  family:         'Students & families',
};

const AUDIENCE_VARIANT = {
  'all-students': 'primary',
  '6th-only':     'success',
  '7th-8th-only': 'accent',
  family:         'default',
};

export default function Events() {
  const grouped = useMemo(() => {
    const map = {};
    for (const cat of CATEGORY_ORDER) {
      map[cat] = EVENTS.filter(e => e.category === cat);
    }
    return map;
  }, []);

  return (
    <div className="events-page">
      <PageHero icon="/assets/eagle/page-icons/events.png" title="Events & Traditions" />

      <p className="events-subtitle">
        Eagle pride all year long. Most dates are announced on the{' '}
        <a
          href="https://crms.leanderisd.org/calendar"
          target="_blank"
          rel="noopener noreferrer"
        >
          CRMS campus calendar ↗
        </a>{' '}
        each August — bookmark it and subscribe to ParentSquare for real-time updates.
      </p>

      {CATEGORY_ORDER.map(cat => {
        const items = grouped[cat];
        if (!items || items.length === 0) return null;
        const isCurriculum = cat === 'curriculum';

        return (
          <section
            key={cat}
            className={`events-section${isCurriculum ? ' events-section--curriculum' : ''}`}
            aria-labelledby={`events-heading-${cat}`}
          >
            <h2 className="events-section-heading" id={`events-heading-${cat}`}>
              {CATEGORY_LABELS[cat]}
            </h2>

            {isCurriculum && (
              <div className="events-curriculum-callout" role="note">
                <strong>Heads up, 6th-grade families:</strong> There are two separate
                programs here. Read both entries carefully — the opt-in requirement
                for 7th &amp; 8th grade catches families off guard every year.
              </div>
            )}

            <ul className="events-list">
              {items.map(event => (
                <li
                  key={event.id}
                  className={`event-card${isCurriculum ? ' event-card--curriculum' : ''}`}
                >
                  <div className="event-card-header">
                    <h3 className="event-card-name">{event.name}</h3>
                    <InfoIcon source={event.source} />
                  </div>

                  <div className="event-card-meta">
                    <Pill
                      variant={AUDIENCE_VARIANT[event.whoCanAttend] || 'default'}
                      size="sm"
                    >
                      {AUDIENCE_LABELS[event.whoCanAttend] || event.whoCanAttend}
                    </Pill>
                    <span className="event-card-when">
                      {event.date
                        ? new Date(event.date + 'T12:00:00').toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : (event.whenLabel || 'TBA — check campus calendar in August')}
                    </span>
                  </div>

                  <p className="event-card-description">{event.description}</p>

                  {event.parentAction && (
                    <div className="event-card-action" role="note">
                      <span className="event-card-action-label">Parent action required</span>
                      <span className="event-card-action-text">{event.parentAction}</span>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        );
      })}

      <div className="events-calendar-footer">
        <p>
          Most dates above are TBA until August. Subscribe to the{' '}
          <a
            href="https://crms.leanderisd.org/calendar"
            target="_blank"
            rel="noopener noreferrer"
          >
            CRMS campus calendar ↗
          </a>{' '}
          and turn on ParentSquare notifications so you don't miss announcement messages.
        </p>
      </div>
    </div>
  );
}
